import {
  skipCurrentTaskInServiceRequest,
  undoPreviousStepServiceRequest,
} from '@commenda/api/service-request';
import {
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToggle,
} from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import { ServiceRequestDetails, TaskWithSLA } from '@commenda/types/src/service-request/types';
import { cn } from '@commenda/utils';
import { ServiceRequestModel } from '@commenda/utils/models/service-request';
import { ServiceSubcategory, TaskStatus } from '@prisma/client';
import { KeyedMutator } from 'swr';
import { useNotification } from '../../../../../hooks/useNotification';
import { SkipAndReopenTaskConfirmationModal } from '../../../../common/modals/SkipAndReopenTaskConfirmationModal';

function ServiceRequestSkipAndUndoEllipsisButton({
  serviceRequest,
  task,
  mutate,
}: {
  serviceRequest: ServiceRequestDetails;
  task: TaskWithSLA;
  mutate: KeyedMutator<unknown>;
}) {

  const { setNotification } = useNotification();

  const confirmationModal = useToggle(false);

  const isSupportedType =
    ServiceRequestModel.supportedServicesForSkipAndUndo.includes(
      getServiceRequestServiceId(serviceRequest) ?? ''
    ) ||
    serviceRequest?.commendaService?.subcategory ===
      ServiceSubcategory.INCORPORATION;

  const isTaskCompleted = task.status === TaskStatus.COMPLETED;

  const handleSkip = async (reason: string) => {
    const res = await skipCurrentTaskInServiceRequest(serviceRequest.id, reason);
    if (!res.ok) {
      setNotification({
        title: 'Error',
        message: 'Failed to skip service request operation.',
        show: true,
        valid: false,
      });
      return;
    }

    confirmationModal.hide();

    mutate();

    setNotification({
      title: 'Success',
      message: `${task.title} skipped successfully.`,
      show: true,
      valid: true,
    });
  };

  const handleUndo = async (reason: string) => {
    const res = await undoPreviousStepServiceRequest(serviceRequest.id, reason);
    if (!res.ok) {
      setNotification({
        title: 'Error',
        message: 'Failed to undo service request operation.',
        show: true,
        valid: false,
      });
    }

    confirmationModal.hide();

    mutate();

    setNotification({
      title: 'Success',
      message: `${task.title} reopened successfully.`,
      show: true,
      valid: true,
    });
  };

  return (
    <>
      {isSupportedType && (
        <Popover>
          <PopoverContent className="w-[200px] p-0" align="end">
            <div className="py-1 flex flex-col">
              <div
                className={cn(
                  'flex items-center h-10 px-4 hover:bg-bg-secondary cursor-pointer'
                )}
                onClick={() => confirmationModal.show()}
              >
                <span className="txt-small-medium text-text-error">
                  {task.status === TaskStatus.COMPLETED
                    ? 'Reopen task'
                    : 'Skip task'}
                </span>
              </div>
            </div>
          </PopoverContent>
          <PopoverTrigger>
            <div
              className={cn(
                'size-8 flex justify-center items-center group cursor-pointer rounded-md hover:bg-button-tertiary-bg-hover transition'
              )}
            >
              <Icons.DotsVertical className="size-5 text-button-tertiary-fg group-hover:text-button-tertiary-fg-hover" />
            </div>
          </PopoverTrigger>
        </Popover>
      )}

      <SkipAndReopenTaskConfirmationModal
        isOpen={confirmationModal.visible}
        closeModal={confirmationModal.hide}
        onContinue={isTaskCompleted ? handleUndo : handleSkip}
        buttonConfirmationText={isTaskCompleted ? 'Reopen task' : 'Skip task'}
        serviceRequest={serviceRequest}
        currentTaskTitle={task?.title ?? ''}
      />
    </>
  );
}

export { ServiceRequestSkipAndUndoEllipsisButton };
